import { useEffect } from 'react';
import { useAuth } from '@leagueplatform/auth-utils';
import { useIntl } from '@leagueplatform/locales';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import {
  DFD_WIDGET_GUTTER,
  DfdEmbedFrame,
} from 'components/dfd-embed-frame/dfd-embed-frame.component';
import { FullScreenContainer } from 'components/full-screen-container/full-screen-container';
import { useDfdEmbedTarget } from 'hooks/use-dfd-embed-target.hook';
import {
  capturePostLoginRedirect,
  consumePostLoginRedirect,
} from 'utils/post-login-redirect';

// Unlike the chat popup, this page has no header row above the widget to give it top space.
const CHAT_PAGE_WIDGET_GUTTER = {
  ...DFD_WIDGET_GUTTER,
  paddingTop: '$one',
} as const;

const useSuppressSmartBanner = () => {
  useEffect(() => {
    const existing = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-itunes-app"]',
    );

    const meta = existing ?? document.createElement('meta');
    if (!existing) {
      meta.name = 'apple-itunes-app';
      document.head.appendChild(meta);
    }

    const previousContent = meta.content;
    meta.content = '';

    return () => {
      meta.content = previousContent;
      if (!existing) {
        meta.remove();
      }
    };
  }, []);
};

export const ChatPage = () => {
  const { isAuthenticated, error } = useAuth({
    redirectToLogInWhenSignedOut: true,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: 'STR_CHAT_SUPPORT_MODAL_TITLE' });
  const embedTarget = useDfdEmbedTarget();
  useSuppressSmartBanner();

  // This route sits outside AuthSessionContainer, so it has to capture its own
  // deep link: `useAuth` redirects to Auth0, which only ever returns to the
  // origin, and the landing page restores this value once authenticated.
  // Captured on mount rather than on `isAuthenticated === false` so the write
  // can't lose the race against `loginWithRedirect` leaving the page.
  useEffect(() => {
    if (isAuthenticated) {
      // Authenticated: nothing to restore, so drop our own breadcrumb.
      consumePostLoginRedirect();
      return;
    }
    capturePostLoginRedirect(
      location.pathname + location.search + location.hash,
    );
  }, [isAuthenticated, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (error) {
      // The session is unusable and can't be recovered in place, so send the
      // user through a fresh login rather than leaving them on a broken page.
      navigate(APP_MANAGED_ROUTES.SIGN_IN, { replace: true });
    }
  }, [error, navigate]);

  if (!isAuthenticated) {
    return <LoadingIndicator />;
  }

  return (
    <FullScreenContainer
      css={embedTarget.widgetId ? CHAT_PAGE_WIDGET_GUTTER : undefined}
    >
      <DfdEmbedFrame
        id="chatPage"
        title={title}
        {...embedTarget}
        style={{
          border: 'none',
        }}
      />
    </FullScreenContainer>
  );
};
